/**
 * The bug reporter's black box. Installed once at startup (main.tsx), it keeps
 * a rolling log of what happened in this tab: clicks, form edits, route
 * changes, console warnings and errors, uncaught exceptions, and every /api
 * request with its status and timing. A report takes the slice that matters —
 * the minute or so before the dialog opened for a written report, everything
 * from just before "Start recording" to "Stop" for a recording — so even a
 * one-line report arrives with the failed request that caused it.
 *
 * Nothing here reads a password field, and request/response bodies are never
 * kept except the first few hundred characters of an error response, which is
 * where the server says what went wrong.
 */

export interface CapturedEvent {
  /** Date.now() when it happened. */
  t: number;
  type: 'click' | 'input' | 'key' | 'nav' | 'console' | 'error' | 'network';
  [key: string]: unknown;
}

const MAX_EVENTS = 3000;
const events: CapturedEvent[] = [];
let installed = false;

/** Marks the reporter's own UI, so its clicks and DOM stay out of reports. */
export const REPORTER_ATTR = 'data-bug-reporter';

function push(event: CapturedEvent) {
  events.push(event);
  if (events.length > MAX_EVENTS) events.splice(0, events.length - MAX_EVENTS);
}

function clip(value: string, max = 300) {
  const flat = value.replace(/\s+/g, ' ').trim();
  return flat.length > max ? `${flat.slice(0, max)}…` : flat;
}

function isReporterUi(el: Element | null) {
  if (!el) return false;
  if (el.closest(`[${REPORTER_ATTR}]`)) return true;
  // The report dialog's title bar and footer sit outside the marked body.
  return Boolean(el.closest('[role="dialog"]')?.querySelector(`[${REPORTER_ATTR}]`));
}

/**
 * A short, readable CSS path: up to four ancestors, each named by id,
 * data-testid, aria-label or its first two classes. Good enough for a human or
 * an agent to find the element in the source; not meant to be unique.
 */
export function describeElement(el: Element): string {
  const parts: string[] = [];
  let node: Element | null = el;
  while (node && node !== document.body && parts.length < 4) {
    let part = node.tagName.toLowerCase();
    const testId = node.getAttribute('data-testid');
    const aria = node.getAttribute('aria-label');
    if (node.id && !/^radix-|^:r/.test(node.id)) {
      part += `#${node.id}`;
      parts.unshift(part);
      break;
    } else if (testId) part += `[data-testid="${testId}"]`;
    else if (aria) part += `[aria-label="${clip(aria, 40)}"]`;
    else {
      const classes = Array.from(node.classList)
        .filter((c) => !c.includes(':') && !c.includes('[') && c.length < 30)
        .slice(0, 2);
      if (classes.length) part += `.${classes.join('.')}`;
    }
    parts.unshift(part);
    node = node.parentElement;
  }
  return parts.join(' > ');
}

function interactiveAncestor(el: Element): Element {
  return el.closest('button, a, [role="button"], [role="menuitem"], [role="tab"], [role="option"], label, input, select, textarea, summary') ?? el;
}

function fieldLabel(el: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement) {
  const byFor = el.id ? document.querySelector(`label[for="${CSS.escape(el.id)}"]`) : null;
  return clip(
    el.getAttribute('aria-label') ||
      byFor?.textContent ||
      el.closest('label')?.textContent ||
      el.getAttribute('placeholder') ||
      el.getAttribute('name') ||
      '',
    80
  );
}

function stringifyArg(arg: unknown): string {
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  if (typeof arg === 'string') return arg;
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
}

function installDomListeners() {
  document.addEventListener(
    'click',
    (e) => {
      const target = e.target instanceof Element ? e.target : null;
      if (!target || isReporterUi(target)) return;
      const el = interactiveAncestor(target);
      const text =
        el.getAttribute('aria-label') ||
        (el instanceof HTMLElement ? el.innerText : el.textContent) ||
        '';
      push({
        t: Date.now(),
        type: 'click',
        selector: describeElement(el),
        text: clip(text, 80),
        x: Math.round(e.clientX),
        y: Math.round(e.clientY)
      });
    },
    true
  );

  document.addEventListener(
    'change',
    (e) => {
      const el = e.target;
      if (
        !(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) ||
        isReporterUi(el)
      )
        return;
      const secret = el instanceof HTMLInputElement && (el.type === 'password' || el.autocomplete?.includes('cc-'));
      const value =
        el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')
          ? String(el.checked)
          : el.value;
      push({
        t: Date.now(),
        type: 'input',
        selector: describeElement(el),
        label: fieldLabel(el),
        value: secret ? '[hidden]' : clip(value, 100)
      });
    },
    true
  );

  document.addEventListener(
    'keydown',
    (e) => {
      const named = e.key === 'Enter' || e.key === 'Escape' || e.metaKey || e.ctrlKey;
      if (!named || ['Meta', 'Control', 'Shift', 'Alt'].includes(e.key)) return;
      const target = e.target instanceof Element ? e.target : document.body;
      if (isReporterUi(target)) return;
      const combo = `${e.metaKey ? '⌘' : ''}${e.ctrlKey ? 'Ctrl+' : ''}${e.key}`;
      push({ t: Date.now(), type: 'key', key: combo, selector: describeElement(target) });
    },
    true
  );
}

function installNavigation() {
  const record = () =>
    push({ t: Date.now(), type: 'nav', to: location.pathname + location.search + location.hash });
  for (const method of ['pushState', 'replaceState'] as const) {
    const original = history[method].bind(history);
    history[method] = (...args: Parameters<History['pushState']>) => {
      const before = location.href;
      original(...args);
      if (location.href !== before) record();
    };
  }
  window.addEventListener('popstate', record);
  record();
}

function installConsole() {
  for (const level of ['error', 'warn'] as const) {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      push({ t: Date.now(), type: 'console', level, message: clip(args.map(stringifyArg).join(' '), 1000) });
      original(...args);
    };
  }
  window.addEventListener('error', (e) => {
    push({
      t: Date.now(),
      type: 'error',
      message: clip(e.message || String(e.error), 500),
      source: e.filename ? `${e.filename}:${e.lineno}:${e.colno}` : undefined,
      stack: e.error instanceof Error ? clip(e.error.stack ?? '', 2000) : undefined
    });
  });
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason;
    push({
      t: Date.now(),
      type: 'error',
      message: clip(`Unhandled rejection: ${stringifyArg(reason)}`, 500),
      stack: reason instanceof Error ? clip(reason.stack ?? '', 2000) : undefined
    });
  });
}

function installFetch() {
  const original = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const started = Date.now();
    const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const rawUrl = input instanceof Request ? input.url : String(input);
    let url = rawUrl;
    let sameOrigin = true;
    try {
      const parsed = new URL(rawUrl, location.href);
      sameOrigin = parsed.origin === location.origin;
      url = sameOrigin ? parsed.pathname + parsed.search : parsed.origin + parsed.pathname;
    } catch {
      /* keep the raw string */
    }
    // Our own upload and the admin's media requests are not part of the bug.
    const ignored = url.startsWith('/api/bug-reports') || url.startsWith('/api/admin/');
    try {
      const res = await original(input, init);
      if (!ignored && (sameOrigin || !res.ok)) {
        const event: CapturedEvent = { t: started, type: 'network', method, url, status: res.status, ms: Date.now() - started };
        if (!res.ok) {
          // The error body is where the server explains itself. Read a copy so
          // the caller still gets an unread response.
          res
            .clone()
            .text()
            .then((body) => {
              event.response = clip(body, 500);
            })
            .catch(() => {});
        }
        push(event);
      }
      return res;
    } catch (error) {
      // React Query cancels superseded requests all the time; that is not a bug.
      const aborted = error instanceof DOMException && error.name === 'AbortError';
      if (!ignored && !aborted) {
        push({ t: started, type: 'network', method, url, error: clip(stringifyArg(error), 200), ms: Date.now() - started });
      }
      throw error;
    }
  };
}

export function installBugReportCollector() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  installDomListeners();
  installNavigation();
  installConsole();
  installFetch();
}

/** Events since `from` (ms epoch), oldest first. */
export function eventsSince(from: number): CapturedEvent[] {
  return events.filter((e) => e.t >= from);
}

/**
 * The page as the user saw it: the live DOM with scripts and the reporter's own
 * UI removed, form values written into attributes (passwords excepted) so the
 * snapshot shows what was typed, and a <base> so stylesheets still resolve when
 * it is opened from the admin page.
 */
export function snapshotHtml(): string {
  const clone = document.documentElement.cloneNode(true) as HTMLElement;

  const live = document.documentElement.querySelectorAll('input, textarea, select');
  const copies = clone.querySelectorAll('input, textarea, select');
  live.forEach((el, i) => {
    const copy = copies[i];
    if (!copy) return;
    if (el instanceof HTMLInputElement) {
      if (el.type === 'password') return;
      if (el.type === 'checkbox' || el.type === 'radio') {
        if (el.checked) copy.setAttribute('checked', '');
        else copy.removeAttribute('checked');
      } else copy.setAttribute('value', el.value);
    } else if (el instanceof HTMLTextAreaElement) {
      copy.textContent = el.value;
    } else if (el instanceof HTMLSelectElement) {
      copy.querySelectorAll('option').forEach((option, j) => {
        if (j === el.selectedIndex) option.setAttribute('selected', '');
        else option.removeAttribute('selected');
      });
    }
  });

  clone.querySelectorAll(`script, noscript, [${REPORTER_ATTR}]`).forEach((el) => el.remove());
  const head = clone.querySelector('head');
  if (head) {
    const base = document.createElement('base');
    base.href = `${location.origin}/`;
    head.prepend(base);
  }
  return `<!doctype html>\n${clone.outerHTML}`;
}

/** Facts about the browser that explain most "works for me" bugs. */
export function environmentContext() {
  return {
    url: location.href,
    title: document.title,
    viewport: { width: window.innerWidth, height: window.innerHeight, dpr: window.devicePixelRatio },
    screen: { width: window.screen.width, height: window.screen.height },
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
    online: navigator.onLine
  };
}
